import { FormEvent, ReactNode } from "react";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";

import { Button } from "../design-system/button";
import { ErrorMessage } from "../design-system/error-message";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { HeaderLG } from "../design-system/typography";
import { SignupStep } from "./signupForm";
import { SignupStepper } from "./SignupStepper";
import { cn } from "@verse_core/shared_components/ClassName";

type SignupStepFrameProps = {
  step: SignupStep;
  title: string;
  // Small grey line above the title, e.g. the clinician's email.
  eyebrow?: string;
  // "greeting" shows the eyebrow larger and in the brand color, e.g. "Hello Sean!".
  eyebrowStyle?: "muted" | "greeting";
  // A 320px centered column for the title and content, for a short form.
  narrow?: boolean;
  onBack: () => void;
  onNext: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  // A problem with the whole step, e.g. submitting failed; shown above the buttons.
  error?: string | null;
  children?: ReactNode;
};

/** A numbered sign-up step: the step indicator, the title and content, and the
 * Back / Next buttons. Steps own their validation through onNext and
 * nextDisabled; Enter in any field submits like Next. */
export const SignupStepFrame = ({
  step,
  title,
  eyebrow,
  eyebrowStyle = "muted",
  narrow = false,
  onBack,
  onNext,
  nextLabel = "Next step",
  nextDisabled = false,
  error = null,
  children,
}: SignupStepFrameProps) => {
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!nextDisabled) {
      onNext();
    }
  };

  return (
    <form
      className="tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-[48px] tw-pt-[24px]"
      onSubmit={handleSubmit}
    >
      <SignupStepper current={step} />
      <div className={cn("tw-w-full", narrow && "tw-max-w-[320px]")}>
        {eyebrow && (
          <p
            className={cn(
              "tw-m-0 tw-font-sohne tw-leading-[1.4]",
              eyebrowStyle === "greeting"
                ? "tw-mb-[4px] tw-text-[18px] tw-font-light tw-tracking-[-0.18px] tw-text-verse-text-brand"
                : "tw-mb-[8px] tw-text-[14px] tw-text-verse-text-tertiary",
            )}
          >
            {eyebrow}
          </p>
        )}
        <HeaderLG>{title}</HeaderLG>
        {children}
        {error && (
          <ErrorMessage role="alert" className="tw-mt-[16px]">
            {error}
          </ErrorMessage>
        )}
      </div>
      <div className="tw-flex tw-w-full tw-items-center tw-justify-between">
        <Button type="button" colorScheme="neutral" size="md" onClick={onBack}>
          <FontAwesomeIcon
            icon={faArrowLeft}
            className="tw-h-[16px] tw-w-[16px] tw-text-verse-icon-primary"
          />
          Back
        </Button>
        <Button
          type="submit"
          colorScheme="tide"
          size="md"
          disabled={nextDisabled}
        >
          {nextLabel}
          <FontAwesomeIcon
            icon={faArrowRight}
            className="tw-h-[16px] tw-w-[16px]"
          />
        </Button>
      </div>
    </form>
  );
};
